import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box, Button, Flex, Heading, Text, Spinner, useToast, VStack, Icon,
} from "@chakra-ui/react";
import { FaCrown } from "react-icons/fa";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { MembershipEnroll } from "./MembershipPage";

/**
 * JoinMembershipPage — the "membership-first" customer journey.
 *
 * A parallel entry point (route /:laundryId/user/join) where the customer
 * enrolls in a plan FIRST, then continues to place an order. Contrast with the
 * existing address-first flow, which starts at the address page and merely
 * OFFERS a plan mid-flow — that path is unchanged.
 *
 * Reuses the exact same backend endpoints and the <MembershipEnroll> component
 * as the standalone MembershipPage; the only new behavior is the ordering of
 * the journey (enroll -> then order) and the post-enroll hand-off into the
 * normal order flow, where membership pricing is applied automatically
 * server-side (keyed on customerId + laundryId).
 */
const API = process.env.REACT_APP_AWS_API_URL;

const JoinMembershipPage = (props) => {
    const params = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const laundryId = props.laundryId || params.laundryId;
    const customerId = props.customerId || localStorage.getItem("customerId");
    const authToken = localStorage.getItem("idToken");

    const [loading, setLoading] = useState(true);
    const [stripePromise, setStripePromise] = useState(null);
    const [plans, setPlans] = useState([]);
    const [membership, setMembership] = useState(null);

    // Where the customer continues after enrolling (or skipping).
    const goToOrder = useCallback(
        () => navigate(`/${laundryId}/user/schedule-order`),
        [navigate, laundryId]
    );

    const loadStripeKey = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/laundry/get-info`, {
                params: { operation: "getLaundryInfo", laundryId, isCustomer: true },
                headers: { "x-api-key": authToken },
            });
            const key = res.data?.body?.stripePublicKey || res.data?.stripePublicKey;
            if (key) setStripePromise(loadStripe(key));
        } catch (err) { /* card capture just won't render without a key */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [laundryId]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/membership/plans`, {
                params: { laundryId, customerId },
            });
            setPlans(res.data?.plans || []);
            setMembership(res.data?.membership || null);
        } catch (err) {
            toast({ title: "Could not load memberships", status: "error", duration: 3000 });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [laundryId, customerId]);

    useEffect(() => {
        if (laundryId) { loadStripeKey(); loadData(); }
    }, [laundryId, loadStripeKey, loadData]);

    if (loading) return <Flex justify="center" p={10}><Spinner /></Flex>;

    // Already a member — no need to enroll again; send them straight to ordering.
    const isMember = !!membership;

    return (
        <Box maxW="720px" mx="auto" p={{ base: 4, md: 6 }}>
            <Flex align="center" gap={2} mb={1}>
                <Icon as={FaCrown} color="yellow.500" />
                <Heading size="lg">Join a Membership</Heading>
            </Flex>
            <Text color="gray.600" mb={5}>
                Start with a prepaid monthly plan and save on every order. Enroll now,
                then place your first order — your plan pricing applies automatically.
            </Text>

            {isMember ? (
                <VStack align="stretch" spacing={4}>
                    <Box p={5} borderWidth="1px" borderRadius="lg" bg="green.50">
                        <Heading size="md" mb={1}>
                            You're a {membership.planName ?? membership.plan_name} member
                        </Heading>
                        <Text color="gray.600">
                            {Number(membership.balanceLb ?? membership.balance_lb ?? 0)} lb available.
                            Your membership pricing applies to your next order automatically.
                        </Text>
                    </Box>
                    <Button colorScheme="green" size="lg" onClick={goToOrder}>
                        Continue to place an order
                    </Button>
                </VStack>
            ) : !customerId ? (
                <Text color="gray.500">Please sign in to subscribe.</Text>
            ) : plans.length === 0 ? (
                <VStack align="stretch" spacing={4}>
                    <Text color="gray.500">No membership plans are available right now.</Text>
                    <Button colorScheme="blue" onClick={goToOrder}>Continue to place an order</Button>
                </VStack>
            ) : stripePromise ? (
                <VStack align="stretch" spacing={5}>
                    <Elements stripe={stripePromise}>
                        <MembershipEnroll
                            laundryId={laundryId}
                            customerId={customerId}
                            plans={plans}
                            onEnrolled={() => {
                                toast({
                                    title: "You're a member!",
                                    description: "Let's place your first order.",
                                    status: "success", duration: 3000,
                                });
                                goToOrder();
                            }}
                        />
                    </Elements>
                    <Button variant="ghost" size="sm" onClick={goToOrder}>
                        Maybe later — just place an order
                    </Button>
                </VStack>
            ) : (
                <VStack align="stretch" spacing={4}>
                    <Text color="gray.500">
                        Payment is temporarily unavailable. Please try again later.
                    </Text>
                    <Button colorScheme="blue" onClick={goToOrder}>Continue to place an order</Button>
                </VStack>
            )}
        </Box>
    );
};

export default JoinMembershipPage;
